import React, { useEffect, useMemo, useState } from "react";
import { ArrowClockwiseIcon, ShieldWarningIcon } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { fetchJob, getJson, latestOf, loadEvidence, useLdc01Access } from "@/lib/ldc01";
import { deriveGates } from "@/lib/ldc01Gates";
import { Panel } from "@/components/ldc01/Ldc01Ui";
import { Stepper } from "@/components/ldc01/Stepper";
import { ReviewStep } from "@/components/ldc01/ReviewStep";
import { VerifyStep } from "@/components/ldc01/VerifyStep";
import { ExecuteStep } from "@/components/ldc01/ExecuteStep";
import { ResultStep } from "@/components/ldc01/ResultStep";

function Unavailable({ error }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="ldc01-unavailable">
      <div className="max-w-md text-center">
        <ShieldWarningIcon size={36} className="mx-auto text-zinc-500 mb-3" />
        <h1 className="text-xl font-semibold text-white mb-2">Live Data Cleanup is not available</h1>
        <p className="text-sm text-zinc-400">The server did not confirm access. The service may be disabled, or your role lacks the dedicated cleanup permission.</p>
        {error && <p className="text-xs text-zinc-500 mt-3 font-mono" data-testid="ldc01-unavailable-detail">HTTP {error.code || "—"} · {error.detail}</p>}
      </div>
    </div>
  );
}

function useEvidence(status) {
  const [ev, setEv] = useState({ dry: null, scan: null, result: null, recon: null, extra: {}, error: null, loading: true });
  useEffect(() => {
    if (!status) return;
    let off = false;
    const jobs = status.jobs || [];
    const ids = { dry: latestOf(jobs, ["dry-run"]), scan: latestOf(jobs, ["scan", "dry-run"]), result: latestOf(jobs, ["execute", "restore"]) };
    const idOf = (j) => (j ? String(j._id || j.id).replace(/^job:/, "") : null);
    const uniq = [...new Set(Object.values(ids).map(idOf).filter(Boolean))];
    const rid = idOf(ids.result);
    Promise.all([Promise.all(uniq.map(fetchJob)), loadEvidence(),
      rid ? getJson(`/admin/ldc01/jobs/${encodeURIComponent(rid)}/reconciliation`) : Promise.resolve({ data: null })])
      .then(([res, extra, rec]) => {
        if (off) return;
        const by = Object.fromEntries(uniq.map((id, i) => [id, res[i]]));
        const pick = (k) => (idOf(ids[k]) ? by[idOf(ids[k])] : { job: null });
        const [d, s, r] = [pick("dry"), pick("scan"), pick("result")];
        const firstExtraErr = Object.entries(extra.errors)[0];
        const error = d.error || s.error || r.error || rec.error
          || (firstExtraErr ? { ...firstExtraErr[1], detail: `${firstExtraErr[0]}: ${firstExtraErr[1].detail}` } : null);
        setEv({ dry: d.job, scan: s.job, result: r.job, recon: rec.data, extra, error, loading: false });
      });
    return () => { off = true; };
  }, [status]);
  return ev;
}

export default function LiveDataCleanup() {
  const { user } = useAuth();
  const access = useLdc01Access();
  const [step, setStep] = useState("review");
  const ev = useEvidence(access.status);
  const gates = useMemo(() => deriveGates(access.status, ev.dry, ev.result, ev.extra), [access.status, ev.dry, ev.result, ev.extra]);
  const env = ev.extra?.environment;

  if (access.phase === "loading") return <div className="p-8 text-sm text-zinc-500" data-testid="ldc01-loading">Checking server access…</div>;
  if (access.phase !== "ok") return <Unavailable error={access.error} />;

  return (
    <div className="p-4 sm:p-8 max-w-5xl space-y-6" data-testid="ldc01-page">
      <header className="space-y-3">
        <div className="overline text-red-400/80">Administrator · Controlled Maintenance</div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">Live Data Cleanup</h1>
            <p className="text-sm text-zinc-400 mt-1">One-time removal of fixed LDC01 trial records. Review → Verify → Execute → Result.</p>
          </div>
          <button type="button" onClick={access.reload} data-testid="ldc01-refresh"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-[var(--border)] text-xs text-zinc-300 hover:text-white hover:bg-[var(--bg-2)]">
            <ArrowClockwiseIcon size={14} /> Refresh from server
          </button>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px] font-mono" data-testid="ldc01-env-banner">
          <span className="px-2 py-1 rounded-sm border border-[var(--border)] text-zinc-300">Server DB: {access.status?.db}</span>
          <span className="px-2 py-1 rounded-sm border border-dashed border-zinc-600 text-zinc-400" data-testid="ldc01-env-identity">
            Environment identity: {env ? `${env.overall} · setting ${env.setting || "absent"} · record ${env.record_present ? "present" : "absent"}` : "NOT VERIFIED by server"}
          </span>
          <span className="px-2 py-1 rounded-sm border border-[var(--border)] text-zinc-500">Browser host (info only): {window.location.hostname}</span>
          <span className="px-2 py-1 rounded-sm border border-[var(--border)] text-zinc-500">Read at: {access.at}</span>
        </div>
      </header>
      <Stepper current={step} onSelect={setStep} />
      {ev.error && <Panel><p className="text-sm text-amber-300" data-testid="ldc01-evidence-error">Some evidence could not be read: HTTP {ev.error.code} · {ev.error.detail}</p></Panel>}
      {step === "review" && <ReviewStep status={access.status} evidenceJob={ev.scan} manifest={ev.extra?.manifest} approval={ev.extra?.approval} coordination={ev.extra?.coordination} />}
      {step === "verify" && <VerifyStep gates={gates} />}
      {step === "execute" && <ExecuteStep status={access.status} gates={gates} dryJob={ev.dry} user={user} manifest={ev.extra?.manifest} env={env} />}
      {step === "result" && <ResultStep resultJob={ev.result} recon={ev.recon} />}
    </div>
  );
}
